/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

import ErrorName from '../../error/error-name';

/**
 * Nahrazovač nastavení ovládání zařízení.
 * Stará se o omezení ovládání zařízení dle obsahu datového skladu.
 */
class ReplacerControl {
  /**
   * Vytvoří instanci.
   * @param {ControlManager} controlManager
   * @param {Logger} logger
   */
  constructor(controlManager, logger) {
    this.controlManager = controlManager;
    this.logger = logger;
  }

  /**
   * Provedení specifických reakcí na chyby při změně omezení ovládání.
   * Chyby související s podporou zařízení nejsou důležité
   * a zalogují se s nízkou prioritou.
   * V těchto případech se správce chová, jako by vše proběhlo bez problémů.
   * Ostatní chyby ošetřuje ten, kdo metodu vyvolal.
   * @param {Error} error
   * @private
   * @returns {undefined}
   */
  handleError(error) {
    // Ignorování chyb způsobených nepodporováním omezení ovládání.
    if (error.name === ErrorName.NotSupportedError) {
      return this.logger.warn(error);
    }
    throw error;
  }

  /**
   * Nahradí stávající nastavení ovládání dle obsahu zařízení.
   * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @returns {Promise.<undefined>} Vyřeší se po dokončení změny.
   */
  replace(contentInfo) {
    return new Promise((resolve) =>
      resolve(
        this.replaceLocalLock(contentInfo).then(() =>
          this.replaceRemoteLock(contentInfo)
        )
      )
    );
  }

  /**
   * Nahradí zámek ovládání pro tlačítka umístěné přímo na zařízení.
   * @param {ContentInfo} contentInfo
   * @private
   * @returns {Promise.<undefined>}
   */
  replaceLocalLock(contentInfo) {
    return new Promise((resolve) => {
      const lock = contentInfo.session.localControlLock;
      if (isNil(lock)) {
        return resolve();
      }
      return resolve(
        this.controlManager
          .setLocalLock(lock)
          .catch((error) => this.handleError(error))
      );
    });
  }

  /**
   * Nahradí zámek ovládání pro tlačítka na dálkovém ovládání.
   * @param {ContentInfo} contentInfo
   * @private
   * @returns {Promise.<undefined>}
   */
  replaceRemoteLock(contentInfo) {
    return new Promise((resolve) => {
      const lock = contentInfo.session.remoteControlLock;
      if (isNil(lock)) {
        return resolve();
      }
      return resolve(
        this.controlManager
          .setRemoteLock(lock)
          .catch((error) => this.handleError(error))
      );
    });
  }
}

export default ReplacerControl;
